import React, { useState, useEffect } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';


const PersonalInfoForm = ({
  formData,
  previewUrl,
  handleImageChange,
  handleChange,
  handleSubmit,
  getUserDetails,
  isLoading
}) => {

  const [isEditing, setIsEditing] = useState(false);
  // console.log(formData);

  const isFormDataValid = (formData) => {
    return (
      (previewUrl || formData.profileURL) &&
      formData.title &&
      formData.firstName &&
      formData.lastName &&
      formData.maritalStatus &&
      formData.gender &&
      formData.passportNumber &&
      formData.dateOfBirth &&
      formData.countryOfBirth &&
      formData.religion &&
      formData.fatherName &&
      formData.fatherStatus &&
      formData.fatherCNIC &&
      formData.fatherCNIC.length === 13
    );
  };

  useEffect(() => {
    if (isFormDataValid(formData)) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [isLoading]);


  const validatePassport = (value) => {
    const passportRegex = /^[A-Z]{2}\d{7}$/; // Two uppercase letters followed by seven digits
    return passportRegex.test(value);
  };

  const validateCNIC = (value) => {
    const cnicRegex = /^\d{0,13}$/; // Allows up to 13 digits
    return cnicRegex.test(value);
  };

  const handleCnicChange = (e) => {
    const { value } = e.target;
    if (validateCNIC(value)) {
      handleChange(e); // Updates the form state only if valid
    }
  };

  const handleSave = () => {
    handleSubmit(); // Trigger the handleSubmit function passed in props
    setIsEditing(false); // Exit edit mode
  };

  const handleEdit = () => {
    setIsEditing(true); // Enter edit mode
  };

  

  useGSAP(() => {
    const fields = document.querySelectorAll(".relative");
  
    fields.forEach((field) => {
      const input = field.querySelector("input, select");
      const label = field.querySelector("label");
      const pLabel = field.querySelector("p"); // Optional error message element
  
      if (!input || !label) {
        // console.warn("Missing input or label in field:", field);
        return;
      }
  
      // INITIAL CHECK: If the input is already filled, animate the label to the top.
      if (input.value !== "") {
        gsap.set(label, { top: -10, fontSize: "0.70rem", color: "#6b7280" });
        gsap.set(input, { borderColor: "#6b7280" });
        if (pLabel) {
          pLabel.textContent = `Please enter your ${label.textContent.toLowerCase()}`;
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      }
  
      // Focus event listener
      input.addEventListener("focus", () => {
        gsap.to(label, { top: -10, fontSize: "0.70rem", color: "#22c55e", duration: 0.25 });
        gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
        if (pLabel) {
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      });
  
      // Input event listener
      input.addEventListener("input", () => {
        if (input.value !== "") {
          gsap.to(label, { color: "#22c55e", duration: 0.25 });
          gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
          if (pLabel) {
            pLabel.classList.remove("text-red-500");
            pLabel.classList.add("text-gray-500");
          }
        }
      });
  
      // Blur event listener
      input.addEventListener("blur", () => {
        if (input.value === "") {
          gsap.to(label, { top: 10, fontSize: "1rem", color: "#6b7280", duration: 0.25 });
          gsap.to(input, { borderColor: "#ef4444", duration: 0.25 });
          input.classList.add("error-border");
          if (pLabel) {
            pLabel.textContent = `${label.textContent} Required *`;
            pLabel.classList.add("text-red-500");
          }
        } else {
          gsap.to(label, { top: -10, fontSize: "0.70rem", color: "#6b7280", duration: 0.25 });
          gsap.to(input, { borderColor: "#6b7280", duration: 0.25 });
          input.classList.remove("error-border");
          if (pLabel) {
            pLabel.textContent = `Please enter your ${label.textContent.toLowerCase()}`;
            pLabel.classList.remove("text-red-500");
            pLabel.classList.add("text-gray-500");
          }
        }
      });
    });
  }, [formData, isEditing]);
  

  return (
    <div className="px-10 py-6 pb-10 bg-white rounded shadow-lg w-[72%]">
      {!isEditing && <div className="flex justify-end absolute right-24">
        <button onClick={handleEdit}>
          <i className="fas fa-edit font-thin"></i>
        </button>
      </div>}
      <h2 className="text-gray-800 text-lg font-semibold mb-4">Personal Info</h2>

      {isEditing && (
        <>
          <div>
            <input
              type="file"
              id="imageInput"
              accept=".jpg,.jpeg,.png"
              onChange={handleImageChange}
              className="hidden"
            />
            <button
              onClick={() => document.getElementById('imageInput').click()}
              className="flex items-center text-gray-600 text-xs border border-gray-400 bg-gray-200 px-3.5 py-2 hover:bg-black hover:text-white"
            >
              <i className="fas fa-edit mr-2"></i>
              Edit Picture
            </button>
          </div>

          <div className="mb-6">
            <img
              src={previewUrl || formData.profileURL || "/Assets/default_user.png"}
              alt=""
              className="size-[122px] border border-gray-300"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="relative">
              <label htmlFor="title" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Title</label>
              <select
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              >
                <option value=""></option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Dr.">Dr.</option>
              </select>
              <p className="text-gray-500 text-xs mt-1">Please select a title</p>
            </div>

            <div className="relative">
              <label htmlFor="firstName" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">First Name</label>
              <input
                id="firstName"
                name='firstName'
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              />
              <p className="text-gray-500 text-xs mt-1 hidden">Please enter your first name</p>
            </div>

            <div className="relative">
              <label
                htmlFor="lastName"
                className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1"
              >
                Last Name
              </label>
              <input
                id="lastName"
                name='lastName'
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full p-2 border rounded" />
              <p className="text-gray-500 text-xs mt-1">Please enter your last name</p>
            </div>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">

            <div className="relative">
              <label htmlFor="maritalStatus" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Marital Status</label>
              <select
                id="maritalStatus"
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleChange}
                className="w-full p-2 border rounded">
                <option value=""></option>
                <option value="Married">Married</option>
                <option value="Single">Single</option>
                <option value="Divorced/Separated">Divorced/Separated</option>
                <option value="Widowed">Widowed</option>
              </select>
              <p className="text-gray-500 text-xs mt-1">Please select your marital status</p>
            </div>

            <div className="relative">
              <label htmlFor="gender" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Gender</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full p-2 border rounded">
                <option value=""></option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              <p className="text-gray-500 text-xs mt-1">Please select your gender</p>
            </div>

            <div className="relative">
              <label htmlFor="passportNumber" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Passport Number</label>
              <input
                id="passportNumber"
                name="passportNumber"
                value={formData.passportNumber}
                onChange={handleChange}
                className="w-full p-2 border rounded">

              </input>
              <p className="text-gray-500 text-xs mt-1">Please enter your passport number</p>
            </div>
          </div>


          <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Biographical Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="relative">
              <label htmlFor="dateOfBirth" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Date Of Birth</label>
              <input
                id="dateOfBirth"
                name='dateOfBirth'
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                // className="w-full p-2 border rounded"/>
                className={`w-full p-2 border rounded ${!formData.dateOfBirth && 'text-transparent'}`}
              />
              <p className="text-gray-500 text-xs mt-1">Please select your date of birth</p>
            </div>

            <div className="relative">
              <label htmlFor="countryOfBirth" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Country of Birth</label>
              <input
                id="countryOfBirth"
                name="countryOfBirth"
                value={formData.countryOfBirth}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              />
              <p className="text-gray-500 text-xs mt-1">Please select your country of birth</p>
            </div>

            <div className="relative">
              <label htmlFor="religion" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Religion</label>
              <select
                id="religion"
                name="religion"
                value={formData.religion}
                onChange={handleChange}
                className="w-full p-2 border rounded">
                <option value=""></option>
                <option value="Islam">Islam</option>
                <option value="Hindu">Hindu</option>
                <option value="Christianity">Christianity</option>
                <option value="Judaism">Judaism</option>
                <option value="Other">Other</option>
              </select>
              <p className="text-gray-500 text-xs mt-1">Please select your religion</p>
            </div>
          </div>

          <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Father's Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="relative">
              <label htmlFor="fatherName" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Father Name</label>
              <input
                id="fatherName"
                name='fatherName'
                type="text"
                value={formData.fatherName}
                onChange={handleChange}
                className="w-full p-2 border rounded" />
              <p className="text-gray-500 text-xs mt-1">Please enter your Father Name</p>
            </div>

            <div className="relative">
              <label htmlFor="fatherStatus" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Father Status</label>
              <select
                id="fatherStatus"
                name="fatherStatus"
                value={formData.fatherStatus}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              >
                <option value=""></option>
                <option value="Alive">Alive</option>
                <option value="Deceased">Deceased</option>
              </select>
              <p className="text-gray-500 text-xs mt-1">Please select your father status</p>
            </div>

            <div className="relative">
              <label htmlFor="fatherCNIC" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">Father CNIC</label>
              <input
                type="text"
                id="fatherCNIC"
                name="fatherCNIC"
                value={formData.fatherCNIC}
                onChange={handleCnicChange} // Custom handler for CNIC validation
                className={`w-full p-2 border rounded ${formData.fatherCNIC.length > 0 && formData.fatherCNIC.length < 13 ? "border-red-500" : ""
                  }`}
              />
              <p className={`text-xs mt-1 ${formData.fatherCNIC.length !== 13 && formData.fatherCNIC ? "text-red-500" : "text-gray-500"}`}>
                {formData.fatherCNIC.length !== 13 && formData.fatherCNIC
                  ? "Father CNIC must be exactly 13 digits."
                  : "Please enter a valid CNIC (13 digits)."}
              </p>
            </div>
          </div>


          <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Nationality Info</h2>
          <hr className="border-t border-gray-300 mb-4" />

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
            <div className="font-semibold text-sm">Identification Number</div>
            <div className="font-semibold text-sm">Type</div>
            <div className="font-semibold text-sm">Country</div>
            {/* <div className="font-semibold text-sm text-right mr-48">Actions</div> */}
          </div>

          <hr className="border-t border-gray-300 mb-4" />

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <div>
              <input type="text" value={formData.cnic} className="w-full p-2 border border-gray-300 rounded text-gray-600 bg-gray-100 cursor-not-allowed" readOnly />
            </div>

            <div>
              <input type="text" value="CNIC" className="w-full p-2 border border-gray-300 rounded text-gray-600 bg-gray-100 cursor-not-allowed" readOnly />
            </div>

            <div>
              <input type="text" value={formData.nationality} className="w-full p-2 border border-gray-300 rounded text-gray-600 bg-gray-100 cursor-not-allowed" readOnly />
            </div>

            <div className="absolute right-16 flex justify-center items-start space-x-4 mt-12 mr-5">
              <button
                onClick={handleSave}
                disabled={!((previewUrl || formData.profileURL)
                  && formData.title
                  && formData.firstName
                  && formData.lastName
                  && formData.maritalStatus
                  && formData.gender
                  && formData.passportNumber
                  && formData.dateOfBirth
                  && formData.countryOfBirth
                  && formData.religion
                  && formData.fatherName
                  && formData.fatherStatus
                  && formData.fatherCNIC.length === 13)}
                className={`px-4 py-2 rounded border 
              ${(previewUrl || formData.profileURL)
                    && formData.title
                    && formData.firstName
                    && formData.lastName
                    && formData.maritalStatus
                    && formData.gender
                    && formData.passportNumber
                    && formData.dateOfBirth
                    && formData.countryOfBirth
                    && formData.religion
                    && formData.fatherName
                    && formData.fatherStatus
                    && formData.fatherCNIC.length === 13
                    ? 'bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer'
                    : 'bg-white text-blue-200 border-blue-200 cursor-not-allowed'
                  }`}
              >
                Save & Close
              </button>

            </div>
          </div>
        </>
      )}

      {!isEditing && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4">
            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Title:</h3>
              <span className='text-gray-500 mt-2 ml-1 text-sm'>{formData.title}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">First Name:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.firstName}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Last Name:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.lastName}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Marital Status:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.maritalStatus}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Gender:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.gender}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Passport Number:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.passportNumber}</span>
            </div>
          </div>

          

          <hr />
          <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Biographical Information</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4">
            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Date Of Birth:</h3>
              <span className='text-gray-500 mt-2 ml-1 text-sm'>{formData.dateOfBirth}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Country Of Birth:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.countryOfBirth}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Religion:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.religion}</span>
            </div>
          </div>

          <hr />
          <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Father's Information</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4">
            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Father Name:</h3>
              <span className='text-gray-500 mt-2 ml-1 text-sm'>{formData.fatherName}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Father Status:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.fatherStatus}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Father's CNIC:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.fatherCNIC}</span>
            </div>
          </div>

          <hr />
          <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Nationality Information</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4">
            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">CNIC:</h3>
              <span className='text-gray-500 mt-2 ml-1 text-sm'>{formData.cnic}</span>
            </div>

            <div className="relative">
              <h3 className="text-base text-black font-semibold transform top-2 left-3 bg-white px-1">Country:</h3>
              <span className="text-gray-500 mt-2 ml-1 text-sm">{formData.countryOfBirth}</span>
            </div>
          </div>


        </>
      )}
    </div>
  )
};

export default PersonalInfoForm;
